import React, { useMemo, useRef } from "react";
import { FillPct, Loc } from "./types";
import { nat, fillColor } from "./utils";

type Props = {
  cladire: string;
  data: Loc[];
  resolveNivel: (l: Loc) => FillPct;
  compact?: boolean;
};

function nearestStep(v: number): FillPct {
  const clamped = Math.max(0, Math.min(100, Math.round(v)));
  const steps: FillPct[] = [0, 25, 50, 75, 100];
  let best = 0,
    d = Infinity;
  for (const s of steps) {
    const dd = Math.abs(clamped - s);
    if (dd < d) {
      d = dd;
      best = s;
    }
  }
  return best as FillPct;
}

const Minimap: React.FC<Props> = ({ cladire, data, resolveNivel, compact }) => {
  const rOrder = useMemo(
    () => Array.from(new Set(data.map((d) => d.rand))).sort(nat),
    [data],
  );
  const kOrder = useMemo(
    () => Array.from(new Set(data.map((d) => d.coloana))).sort(nat),
    [data],
  );

  const avgByRK = useMemo(() => {
    const acc = new Map<string, { sum: number; n: number }>();
    for (const l of data) {
      const id = `${l.rand}|${l.coloana}`;
      const cur = acc.get(id) || { sum: 0, n: 0 };
      cur.sum += resolveNivel(l);
      cur.n += 1;
      acc.set(id, cur);
    }
    const out = new Map<string, FillPct>();
    acc.forEach((v, id) => out.set(id, nearestStep(v.n ? v.sum / v.n : 0)));
    return out;
  }, [data, resolveNivel]);

  const cell = 9,
    gap = 2;
  const w = kOrder.length
    ? kOrder.length * cell + (kOrder.length - 1) * gap
    : 0;
  const h = rOrder.length
    ? rOrder.length * cell + (rOrder.length - 1) * gap
    : 0;

  const svgRef = useRef<SVGSVGElement>(null);

  const jumpTo = (r: string, k: string) => {
    const rowEl = document.getElementById(`wm-row-${cladire}-${r}`);
    if (!rowEl) return;
    rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
    const scroller = rowEl.querySelector(
      ".wm-col-scroller",
    ) as HTMLElement | null;
    if (scroller) {
      const kIndex = Math.max(0, kOrder.indexOf(k));
      const ratio = kOrder.length > 1 ? kIndex / (kOrder.length - 1) : 0;
      const target =
        ratio * Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      scroller.scrollTo({ left: target, behavior: "smooth" });
    }
  };

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || !w || !h) return;
    const rect = svg.getBoundingClientRect();
    const x = Math.min(Math.max(0, e.clientX - rect.left), w);
    const y = Math.min(Math.max(0, e.clientY - rect.top), h);
    const col = Math.min(
      kOrder.length - 1,
      Math.max(0, Math.floor(x / (cell + gap))),
    );
    const row = Math.min(
      rOrder.length - 1,
      Math.max(0, Math.floor(y / (cell + gap))),
    );
    const r = rOrder[row],
      k = kOrder[col];
    if (r && k) jumpTo(r, k);
  };

  if (!rOrder.length || !kOrder.length) return null;

  return (
    <div className={`wm-minimap ${compact ? "wm-minimap--compact" : ""}`}>
      <svg
        ref={svgRef}
        className="wm-minimap-svg"
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        onClick={handleClick}
        role="img"
        aria-label={`Minimap ${cladire}`}>
        {rOrder.map((r, ri) =>
          kOrder.map((k, ki) => {
            const x = ki * (cell + gap),
              y = ri * (cell + gap);
            const v = avgByRK.get(`${r}|${k}`);
            const fill = v != null ? fillColor(v) : "#e5e7eb";
            return (
              <rect
                key={`${r}-${k}`}
                x={x}
                y={y}
                width={cell}
                height={cell}
                rx="2"
                ry="2"
                fill={fill}
                stroke="#d1d5db"
                strokeWidth="0.5"
              />
            );
          }),
        )}
      </svg>
      {!compact && (
        <div className="wm-minimap-legend">
          <span>
            <i className="wm-swatch wm-swatch--0" />
            0%
          </span>
          <span>
            <i className="wm-swatch wm-swatch--25" />
            25%
          </span>
          <span>
            <i className="wm-swatch wm-swatch--50" />
            50%
          </span>
          <span>
            <i className="wm-swatch wm-swatch--75" />
            75%
          </span>
          <span>
            <i className="wm-swatch wm-swatch--100" />
            100%
          </span>
        </div>
      )}
    </div>
  );
};

export default Minimap;
